import type { PortalNotificationFeedItem } from '@/lib/api/client-portal.api'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { formatRelativeTime } from '@/utils/date'
import {
  At,
  ChatCircle,
  CheckSquare,
  FileText,
  Lifebuoy,
  X,
} from '@phosphor-icons/react'

function notificationIcon(type: string) {
  const className = 'h-4 w-4'
  if (type.includes('MENTION') || type === 'COMMENT_REPLY') {
    return <At className={className} />
  }
  if (type.startsWith('TASK') || type === 'CLIENT_PORTAL_TASK_READY') {
    return <CheckSquare className={className} />
  }
  if (type.startsWith('TICKET')) {
    return <Lifebuoy className={className} />
  }
  if (type.includes('NOTE')) {
    return <FileText className={className} />
  }
  return <ChatCircle className={className} />
}

function distinctNotificationMessage(
  title: string,
  message?: string,
): string | undefined {
  const trimmedMessage = message?.trim()
  if (!trimmedMessage) return undefined

  const normalize = (value: string) =>
    value
      .toLocaleLowerCase()
      .replace(/[\p{P}\p{S}]+/gu, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  const normalizedTitle = normalize(title)
  const normalizedMessage = normalize(trimmedMessage)

  if (
    !normalizedMessage ||
    normalizedTitle === normalizedMessage ||
    normalizedTitle.endsWith(normalizedMessage) ||
    (normalizedMessage.length >= 24 &&
      normalizedTitle.includes(normalizedMessage))
  ) {
    return undefined
  }

  return trimmedMessage
}

export function ClientPortalNotificationItem({
  item,
  onOpen,
  onDismiss,
  comfortable = false,
}: {
  item: PortalNotificationFeedItem
  onOpen: () => void
  onDismiss: () => void
  comfortable?: boolean
}) {
  const isUnread = !item.readAt
  const message = distinctNotificationMessage(item.title, item.message)

  return (
    <div
      className={cn(
        'group relative flex rounded-lg transition-colors',
        isUnread ? 'bg-primary/[0.055]' : 'hover:bg-muted/45',
      )}
    >
      <button
        type="button"
        onClick={onOpen}
        className={cn(
          'flex min-w-0 flex-1 items-start gap-3 pr-10 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          comfortable ? 'px-4 py-4' : 'px-3 py-3',
        )}
        aria-label={`${item.title}${isUnread ? ', unread' : ''}`}
      >
        <span
          className={cn(
            'mt-0.5 flex shrink-0 items-center justify-center rounded-full border',
            comfortable ? 'h-10 w-10' : 'h-8 w-8',
            isUnread
              ? 'border-primary/20 bg-primary/10 text-primary'
              : 'border-border/60 bg-muted/30 text-muted-foreground',
          )}
        >
          {notificationIcon(item.type)}
        </span>
        <span className="min-w-0 flex-1">
          <span
            className={cn(
              'block leading-snug text-foreground',
              comfortable ? 'text-[15px]' : 'text-sm',
              isUnread && 'font-medium',
            )}
          >
            {item.title}
          </span>
          {message ? (
            <span className="mt-0.5 line-clamp-2 block text-xs leading-relaxed text-muted-foreground">
              {message}
            </span>
          ) : null}
          <span className="mt-1 block text-[11px] text-muted-foreground">
            {item.actor ? `${item.actor.name} · ` : ''}
            {formatRelativeTime(item.occurredAt)}
          </span>
        </span>
        {isUnread ? (
          <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary" />
        ) : null}
      </button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={event => {
          event.stopPropagation()
          onDismiss()
        }}
        className="absolute right-1.5 top-1.5 h-7 w-7 text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
        aria-label={`Dismiss ${item.title}`}
        title="Dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </Button>
    </div>
  )
}
